import { serializeToMarkdown } from './markdownHelpers';
import config from '../config';

/**
 * Default chunking configuration
 */
export const DEFAULT_CHUNK_CONFIG = {
  minChars: Number(config?.CHUNK?.MIN_CHARS) || 500,
  targetChars: Number(config?.CHUNK?.TARGET_CHARS) || 800,
  maxChars: Number(config?.CHUNK?.MAX_CHARS) || 1500,
  headerLevels: Array.isArray(config?.CHUNK?.HEADER_LEVELS) ? config.CHUNK.HEADER_LEVELS : [1, 2, 3, 4, 5, 6]
};

/**
 * Generate a random chunk ID
 * @returns {string} - 6-character random string
 */
let chunkIdCounter = 0;

function generateChunkId() {
  chunkIdCounter++;
  return (Date.now() + chunkIdCounter + Math.random() * 1000000).toString(36).substr(2, 6);
}

/**
 * Calculate the character length of a TipTap node
 * @param {Object} node - TipTap/ProseMirror node
 * @returns {number} - Character count
 */
function getNodeCharLength(node) {
  if (!node) return 0;

  // Text nodes
  if (node.type === 'text') {
    return node.text?.length || 0;
  }

  // Nodes with content
  if (node.content && Array.isArray(node.content)) {
    return node.content.reduce((sum, child) => sum + getNodeCharLength(child), 0);
  }

  return 0;
}

/**
 * Calculate total character length of an array of nodes
 * @param {Array} nodes - Array of TipTap nodes
 * @returns {number} - Total character count
 */
function getTotalCharLength(nodes) {
  return nodes.reduce((sum, node) => sum + getNodeCharLength(node), 0);
}

function getNonPageNodeCount(nodes) {
  return (nodes || []).reduce((sum, node) => sum + (node?.type === 'page' ? 0 : 1), 0);
}

function getSegmentCharLength(segment) {
  return Number(segment?.charLength || getTotalCharLength(segment?.nodes || []));
}

export function normalizeDocForChunking(docJson, userConfig = {}) {
  void userConfig;
  return docJson;
}

function canPreserveUnavoidableShortSegment(segments, index, config) {
  const current = segments[index];
  const currentChars = getSegmentCharLength(current);
  if (currentChars >= config.minChars) return false;

  const previous = index > 0 ? segments[index - 1] : null;
  const next = index < segments.length - 1 ? segments[index + 1] : null;
  const previousChars = previous ? getSegmentCharLength(previous) : null;
  const nextChars = next ? getSegmentCharLength(next) : null;

  const mergeWithPreviousPossible = previousChars !== null ? (previousChars + currentChars) <= config.maxChars : false;
  const mergeWithNextPossible = nextChars !== null ? (nextChars + currentChars) <= config.maxChars : false;

  return !mergeWithPreviousPossible && !mergeWithNextPossible;
}

function canPreserveUnavoidableLongSegment(segment, config) {
  const segmentChars = getSegmentCharLength(segment);
  if (segmentChars <= config.maxChars) return false;
  const nonPageNodes = (segment?.nodes || []).filter(node => node?.type !== 'page');
  return nonPageNodes.length <= 1;
}

/**
 * Check if a paragraph node is minimal (≤1 char or just <br>)
 * @param {Object} node - TipTap node
 * @returns {boolean}
 */
function isMinimalParagraph(node) {
  if (node.type !== 'paragraph') return false;
  const text = getNodeText(node);
  return text.length <= 1 || text.trim() === '';
}


/**
 * Clean node attributes by removing null values
 * @param {Object} node - TipTap node
 * @returns {Object} - Node with cleaned attrs
 */
function cleanNodeAttrs(node) {
  if (!node || !node.attrs) return node;
  const cleanedAttrs = {};
  for (const [key, value] of Object.entries(node.attrs)) {
    if (value !== null && !['chunk', 'chunkId', 'chunkHash', 'chunkPrev', 'chunkNext'].includes(key)) {
      cleanedAttrs[key] = value;
    }
  }
  if (Object.keys(cleanedAttrs).length > 0) {
    return {
      ...node,
      attrs: cleanedAttrs
    };
  } else {
    const { attrs, ...rest } = node;
    return rest;
  }
}

/**
 * Extract plain text from a single node
 * @param {Object} node - TipTap node
 * @returns {string} - Plain text content
 */
function getNodeText(node) {
  if (!node) return '';

  if (node.type === 'text') {
    return node.text || '';
  }

  if (node.content && Array.isArray(node.content)) {
    return node.content.map(getNodeText).join('');
  }

  return '';
}

function collectTextLeafNodes(node, leaves = []) {
  if (!node || typeof node !== 'object') return leaves;
  if (node.type === 'text') {
    leaves.push(node);
    return leaves;
  }
  if (Array.isArray(node.content)) {
    node.content.forEach((child) => collectTextLeafNodes(child, leaves));
  }
  return leaves;
}

function hasBoldMark(node) {
  const marks = Array.isArray(node?.marks) ? node.marks : [];
  return marks.some((mark) => {
    const markType = String(mark?.type || '').trim().toLowerCase();
    return markType === 'bold' || markType === 'strong';
  });
}

function isPseudoHeaderParagraph(node) {
  if (!node || node.type !== 'paragraph') return false;

  const text = getNodeText(node).replace(/\s+/g, ' ').trim();
  if (!text) return false;
  if (text.length < 4 || text.length > 120) return false;

  const wordCount = text.split(/\s+/).filter(Boolean).length;
  if (wordCount < 1 || wordCount > 14) return false;

  // Avoid treating regular prose sentences as section headers.
  if (/[.!?]$/.test(text)) return false;

  const textLeaves = collectTextLeafNodes(node, []).filter((leaf) => String(leaf?.text || '').trim().length > 0);
  if (textLeaves.length === 0) return false;

  return textLeaves.every((leaf) => hasBoldMark(leaf));
}

// Create chunking configuration with defaults
// Params: userConfig (object)
// Returns: Configuration object (object)
function createChunkingConfig(userConfig = {}) {
  return {
    ...DEFAULT_CHUNK_CONFIG,
    ...userConfig
  };
}

// Check if a node should be skipped during processing
// Params: node (object)
// Returns: Whether to skip the node (boolean)
// Check if a node should be skipped during processing
// Params: node (object)
// Returns: Whether to skip the node (boolean)
function shouldSkipNode(node) {
  return node.type === 'page';
}

// Update header level tracking for chunking decisions
// Params: node (object), currentLevel (number|null)
// Returns: Updated header level or current level (number|null)
function updateHeaderLevel(node, currentLevel) {
  if (node.type === 'heading') {
    const level = node.attrs?.level || 1;
    return currentLevel === null ? level : currentLevel;
  }
  if (isPseudoHeaderParagraph(node)) {
    const level = 3;
    return currentLevel === null ? level : currentLevel;
  }
  return currentLevel;
}

// Find if there is a header within the next few nodes that should trigger a chunk split
// Params: nodes (array), startIndex (number), currentLevel (number|null), type (string)
// Returns: Split decision object or null (object|null)
// Find the best split point across all element types with priorities
// Params: nodes (array), startIndex (number), currentLevel (number|null), currentChars (number), targetChars (number), minChars (number), maxChars (number)
// Returns: Object with index and reason or null (object|null)
function findBestSplit(nodes, startIndex, currentLevel, currentChars, targetChars, minChars, maxChars) {
  const candidates = [];
  const structuredTypes = ['bulletList', 'orderedList', 'table', 'codeBlock', 'blockquote', 'horizontalRule'];
  let accumulated = 0;
  for (let i = 0; startIndex + i < nodes.length; i++) {
    const node = nodes[startIndex + i];
    if (i > 0) accumulated += getNodeCharLength(node);
    const size = currentChars + accumulated;
    if (size > maxChars) break; // Stop searching beyond maxChars
    if (isMinimalParagraph(node)) continue;
    if (node.type === 'heading' || isPseudoHeaderParagraph(node)) {
      const isPseudo = node.type !== 'heading';
      const level = isPseudo ? 3 : (node.attrs?.level || 1);
      if (size >= minChars) {
        if (level === 1) {
          candidates.push({ index: startIndex + i, priority: 1, level, size, reason: isPseudo ? `header-h${level}-pseudo` : `header-h${level}` });
        } else if (currentLevel !== null && level <= currentLevel) {
          candidates.push({ index: startIndex + i, priority: 2, level, size, reason: isPseudo ? `header-h${level}-pseudo` : `header-h${level}` });
        } else {
          candidates.push({ index: startIndex + i, priority: 3, level, size, reason: isPseudo ? `header-h${level}-pseudo` : `header-h${level}` });
        }
      }
    } else if (structuredTypes.includes(node.type)) {
      if (size >= targetChars) {
        candidates.push({ index: startIndex + i, priority: 4, size, reason: 'structured-block' });
      }
    } else if (node.type === 'paragraph') {
      if (size >= targetChars) {
        candidates.push({ index: startIndex + i, priority: 5, size, reason: 'paragraph' });
      }
    }
  }
  if (candidates.length === 0) return null;
  const minPriority = Math.min(...candidates.map(c => c.priority));
  let topCandidates = candidates.filter(c => c.priority === minPriority);
  if (minPriority === 1) { // headers: prioritize lowest level
    const minLevel = Math.min(...topCandidates.map(c => c.level));
    topCandidates = topCandidates.filter(c => c.level === minLevel);
  }
  let best = null;
  let minDiff = Infinity;
  for (const c of topCandidates) {
    const diff = Math.abs(c.size - targetChars);
    if (diff < minDiff) {
      minDiff = diff;
      best = c;
    }
  }
  return { index: best.index, reason: best.reason };
}



function createChunk(content, hash, reason, existingHashes, docId) {
  let chunkHash = hash;
  if (!chunkHash) {
    chunkHash = `${docId}_${generateChunkId()}`;
  }
  let attempts = 0;
  while (existingHashes.has(chunkHash) && attempts < 100) {
    chunkHash = `${docId}_${generateChunkId()}`;
    attempts++;
  }
  if (attempts >= 100) {
    console.warn('Failed to generate unique chunk ID after 100 attempts');
    // Fallback to a guaranteed unique ID
    chunkHash = 'fallback_' + Date.now() + '_' + Math.random();
  }
  existingHashes.add(chunkHash);
  return {
    content: [...content],
    chunkHash,
    splitReason: reason
  };
}

function isChatMessageDoc(nodes = []) {
  const nonPageNodes = (nodes || []).filter(node => node?.type !== 'page');
  if (nonPageNodes.length === 0) return false;
  return nonPageNodes.every(node => node?.type === 'chatMessage');
}

function normalizeChatRole(value) {
  const role = String(value || '').trim().toLowerCase();
  if (role === 'assistant') return 'ai';
  return role;
}

function chunkChatTurns(nodes, existingHashes, docId) {
  const chunks = [];
  const messageNodes = (Array.isArray(nodes) ? nodes : []).filter(node => node?.type !== 'page');
  let currentChunkNodes = [];

  for (const node of messageNodes) {
    currentChunkNodes.push(node);
    const role = normalizeChatRole(node?.attrs?.role);

    // For chat docs, only split at assistant boundaries so turns stay intact.
    if (role === 'ai') {
      chunks.push(createChunk(currentChunkNodes, `${docId}_${generateChunkId()}`, 'chat-turn', existingHashes, docId));
      currentChunkNodes = [];
    }
  }

  if (currentChunkNodes.length > 0) {
    if (chunks.length > 0) {
      const previous = chunks[chunks.length - 1];
      previous.content = [...(previous.content || []), ...currentChunkNodes];
      previous.splitReason = `${previous.splitReason || 'chat-turn'}+pending-turn`;
    } else {
      chunks.push(createChunk(currentChunkNodes, `${docId}_${generateChunkId()}`, 'chat-pending-turn', existingHashes, docId));
    }
  }

  return chunks;
}

export function getChunks(docJson, userConfig = {}, docId) {
  const config = createChunkingConfig(userConfig);
  config.maxChars = Math.min(config.maxChars, 2000); // Enforce hard limit

  if (!docJson?.content?.length) return [];

  const nodes = docJson.content;
  const chunks = [];
  const existingHashes = new Set();

  if (isChatMessageDoc(nodes)) {
    return chunkChatTurns(nodes, existingHashes, docId);
  }

  // First, find all existing chunk boundaries
  const splitPoints = [];
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].attrs?.chunk) {
      splitPoints.push(i);
    }
  }

  // Create segments: [0, split1), [split1, split2), ..., [last, end)
  const segments = [];
  let start = 0;
  for (const split of splitPoints) {
    if (split > start) {
      const freeNodes = nodes.slice(start, split);
      segments.push({
        nodes: freeNodes,
        hasChunkAttr: false,
        charLength: getTotalCharLength(freeNodes)
      });
    }
    // Find the end of this chunk: next split or end
    let end = split + 1;
    while (end < nodes.length && !nodes[end].attrs?.chunk) {
      end++;
    }
    const chunkNodes = nodes.slice(split, end);
    segments.push({
      nodes: chunkNodes,
      hasChunkAttr: true,
      chunkHash: nodes[split].attrs.chunk,
      charLength: getTotalCharLength(chunkNodes)
    });
    start = end;
  }
  if (start < nodes.length) {
    const trailingNodes = nodes.slice(start);
    segments.push({
      nodes: trailingNodes,
      hasChunkAttr: false,
      charLength: getTotalCharLength(trailingNodes)
    });
  }

  // Now process each segment
  for (let segmentIndex = 0; segmentIndex < segments.length; segmentIndex++) {
    const segment = segments[segmentIndex];
    const segmentChars = getSegmentCharLength(segment);
    const preserveExisting = segment.hasChunkAttr && (
      (segmentChars >= config.minChars && segmentChars <= config.maxChars) ||
      canPreserveUnavoidableShortSegment(segments, segmentIndex, config) ||
      canPreserveUnavoidableLongSegment(segment, config)
    );

    if (preserveExisting) {
      // Existing chunk and within size limits: create a single chunk
      const chunkHash = segment.chunkHash;
      if (!existingHashes.has(chunkHash)) {
        const chunkNodes = segment.nodes;
        const charLength = getTotalCharLength(chunkNodes);
        chunks.push({
          chunkHash,
          content: chunkNodes,
          char_length: charLength,
          splitReason: 'existing-chunk-boundary'
        });
        existingHashes.add(chunkHash);
      }
    } else {
      const previousChunk = chunks[chunks.length - 1];
      const previousChars = previousChunk ? getTotalCharLength(previousChunk.content || []) : 0;
      const canMergeIntoPrevious = (
        previousChunk &&
        segmentChars > 0 &&
        segmentChars < config.minChars &&
        previousChars + segmentChars <= config.maxChars
      );

      if (canMergeIntoPrevious) {
        previousChunk.content = [...(previousChunk.content || []), ...(segment.nodes || [])];
        previousChunk.splitReason = `${previousChunk.splitReason || 'chunk'}+merge-undersized-segment`;
        continue;
      }

      // New content or oversized existing chunk: apply normal chunking
      const isLastSegment = segmentIndex === segments.length - 1;
      const segmentChunks = chunkSegment(segment.nodes, config, existingHashes, docId, {
        tailReason: isLastSegment ? 'end-of-document' : 'end-of-segment'
      });
      chunks.push(...segmentChunks);
    }
  }

  return chunks;
}

// Chunk a segment of nodes without considering existing chunk attrs
function chunkSegment(nodes, config, existingHashes, docId, options = {}) {
  const tailReason = options.tailReason || 'end-of-document';
  const workingNodes = [...(nodes || [])];
  const chunks = [];
  let currentIndex = 0;
  let startIndex = 0;
  let chunkNodes = [];
  let accumulatedChars = 0;
  let currentHeaderLevel = null;

  const flushCurrentChunk = (reason) => {
    if (chunkNodes.length === 0) return;

    const currentChars = accumulatedChars;
    const previousChunk = chunks[chunks.length - 1];
    const previousChars = previousChunk ? getTotalCharLength(previousChunk.content || []) : 0;

    if (
      currentChars < config.minChars &&
      previousChunk &&
      previousChars + currentChars <= config.maxChars
    ) {
      previousChunk.content = [...(previousChunk.content || []), ...chunkNodes];
      previousChunk.splitReason = `${previousChunk.splitReason || 'chunk'}+merge-${reason}`;
    } else {
      const chunkHash = `${docId}_${generateChunkId()}`;
      chunks.push(createChunk(chunkNodes, chunkHash, reason, existingHashes, docId));
    }

    chunkNodes = [];
    accumulatedChars = 0;
    currentHeaderLevel = null;
  };

  while (currentIndex < workingNodes.length) {
    const node = workingNodes[currentIndex];

    if (shouldSkipNode(node)) {
      currentIndex++;
      continue;
    }

    const nodeChars = getNodeCharLength(node);

    if (accumulatedChars + nodeChars > config.maxChars && chunkNodes.length > 0) {
      flushCurrentChunk('pre-overflow');
      startIndex = currentIndex;
    }

    chunkNodes.push(node);
    accumulatedChars += nodeChars;
    currentHeaderLevel = updateHeaderLevel(node, currentHeaderLevel);

    if (accumulatedChars > config.maxChars) {
      const hasSingleNode = chunkNodes.length === 1;
      flushCurrentChunk(hasSingleNode ? 'single-node-oversize' : 'max-chars-exceeded');
      startIndex = currentIndex + 1;
      currentIndex++;
      continue;
    }

    if (accumulatedChars >= config.minChars) {
      const result = findBestSplit(
        workingNodes,
        currentIndex,
        currentHeaderLevel,
        accumulatedChars,
        config.targetChars,
        config.minChars,
        config.maxChars
      );
      if (result && result.index > currentIndex) {
        chunkNodes = workingNodes.slice(startIndex, result.index);
        accumulatedChars = getTotalCharLength(chunkNodes);
        flushCurrentChunk(result.reason);
        startIndex = result.index;
        currentIndex = result.index;
        continue;
      }
    }

    currentIndex++;
  }

  if (chunkNodes.length > 0 && getTotalCharLength(chunkNodes) > 0) {
    const tailChars = getTotalCharLength(chunkNodes);
    const previousChunk = chunks[chunks.length - 1];
    const previousChars = previousChunk ? getTotalCharLength(previousChunk.content || []) : 0;

    if (
      tailChars < config.minChars &&
      previousChunk &&
      previousChars + tailChars <= config.maxChars
    ) {
      previousChunk.content = [...(previousChunk.content || []), ...chunkNodes];
      previousChunk.splitReason = `${previousChunk.splitReason || 'chunk'}+tail-merge`;
    } else {
      const chunkHash = `${docId}_${generateChunkId()}`;
      chunks.push(createChunk(chunkNodes, chunkHash, tailReason, existingHashes, docId));
    }
  }

  return chunks;
}

/**
 * Remove chunk attributes from content for consistent hashing
 * @param {Object} content - TipTap content object
 * @returns {Object} - Content with chunk attrs removed
 */
export function removeChunkAttributes(content) {
  if (!content) return content;
  
  if (Array.isArray(content)) {
    return content.map(removeChunkAttributes);
  }
  
  if (typeof content === 'object') {
    const cleaned = {};
    for (const [key, value] of Object.entries(content)) {
      if (key === 'attrs' && value && typeof value === 'object') {
        // Remove all chunk-related attrs from attrs
        const { chunk, chunkId, chunkHash, chunkPrev, chunkNext, ...otherAttrs } = value;
        if (Object.keys(otherAttrs).length > 0) {
          cleaned[key] = otherAttrs;
        }
      } else {
        cleaned[key] = removeChunkAttributes(value);
      }
    }
    return cleaned;
  }
  
  return content;
}

/**
 * Hash a chunk's JSON content for deduplication
 * @param {Object} chunkJson - Chunk JSON object
 * @returns {Promise<string>} - 8-character hash string
 */
export async function hashChunkContent(chunkJson) {
  // Hash only canonical content, not transient chunk metadata
  // like splitReason/chunkHash that can vary between equivalent saves.
  const canonicalInput = (() => {
    if (!chunkJson || typeof chunkJson !== 'object') return chunkJson;
    if (Array.isArray(chunkJson.content)) {
      return {
        type: 'doc',
        content: chunkJson.content
      };
    }
    if (chunkJson.content_json && Array.isArray(chunkJson.content_json.content)) {
      return {
        type: 'doc',
        content: chunkJson.content_json.content
      };
    }
    return chunkJson;
  })();

  // Remove chunk attributes for consistent hashing
  const cleanedContent = removeChunkAttributes(canonicalInput);
  const text = JSON.stringify(cleanedContent);
  
  // Use Web Crypto API for hashing
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const fullHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return fullHash.substring(0, 8);
  }
  
  // Fallback: simple hash for Node.js or older environments
  // Note: This is less secure, mainly for development
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    const char = text.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(16).substring(0, 8);
}

/**
 * Prepare chunks with metrics for database storage
 * @param {Array<Object>} chunks - Array of chunk JSON objects
 * @returns {Promise<Array<Object>>} - Array of { chunkHash, hash, char_length, content_json }
 */

export async function prepareChunksForStorage(chunks) {
  const prepared = await Promise.all(chunks.map(async (chunk) => {
    const hash = await hashChunkContent(chunk);
    // Clean null attrs and chunk attrs from all content nodes for storage
    const cleanedContent = (chunk.content || []).map(cleanNodeAttrs);
    // Add chunk attribute to the first node
    if (cleanedContent.length > 0) {
      cleanedContent[0] = {
        ...cleanedContent[0],
        attrs: {
          ...(cleanedContent[0].attrs || {}),
          chunk: chunk.chunkHash
        }
      };
    }
    const charLength = getTotalCharLength(cleanedContent);
    // Generate Markdown for this chunk (derived, non-canonical)
    const mdDoc = { type: 'doc', content: cleanedContent };
    const contentMd = serializeToMarkdown(mdDoc);
    return {
      chunkHash: chunk.chunkHash,
      hash: hash,
      char_length: charLength,
      content_json: {
        content: cleanedContent
      },
      content_md: contentMd,
      splitReason: chunk.splitReason
    };
  }));
  
  return prepared;
}

// Alias for backward compatibility
export const prepareChunks = prepareChunksForStorage;

/**
 * Extract plain text from document JSON
 * @param {Object} doc - Document JSON with type: 'doc'
 * @returns {string} - Plain text content
 */
export function extractPlainText(doc) {
  if (!doc) return '';
  
  function extractFromNode(node) {
    if (!node) return '';
    
    if (node.type === 'text') {
      return node.text || '';
    }
    
    if (node.content && Array.isArray(node.content)) {
      const text = node.content.map(extractFromNode).join('');
      // Add newlines after block elements
      if (node.type === 'paragraph' || node.type === 'heading') {
        return text + '\n';
      }
      return text;
    }
    
    return '';
  }
  
  return extractFromNode(doc).trim();
}

/**
 * Apply chunk hash attributes to document JSON
 * @param {Object} docJson - Document JSON with type: 'doc'
 * @param {Array} chunks - Array of chunks with content
 * @returns {Object} - Document JSON with chunk attributes applied
 */
export function applyChunkAttributes(docJson, chunks) {
  if (!docJson || !docJson.content || !Array.isArray(docJson.content)) {
    return docJson;
  }
  
  const result = { ...docJson, content: [...docJson.content] };
  
  // Clear existing chunk attributes
  for (let i = 0; i < result.content.length; i++) {
    const node = result.content[i];
    if (node.attrs && node.attrs.chunk) {
      const { chunk, ...otherAttrs } = node.attrs;
      result.content[i] = {
        ...node,
        attrs: otherAttrs
      };
    }
  }
  
  // Apply new attributes
  let nonPageIndex = 0;
  for (const chunk of chunks) {
    if (!chunk.content || !Array.isArray(chunk.content)) continue;
    
    // Find the actual index of the nonPageIndex-th non-page node
    let actualIndex = -1;
    let count = 0;
    for (let i = 0; i < result.content.length; i++) {
      if (result.content[i].type !== 'page') {
        if (count === nonPageIndex) {
          actualIndex = i;
          break;
        }
        count++;
      }
    }
    
    if (actualIndex >= 0 && actualIndex < result.content.length) {
      const node = result.content[actualIndex];
      result.content[actualIndex] = {
        ...node,
        attrs: {
          ...(node.attrs || {}),
          chunk: chunk.chunkHash
        }
      };
    }
    
    nonPageIndex += getNonPageNodeCount(chunk.content);
  }
  
  return result;
}
